<template>
  <Dialog
    v-model="showDialog"
    v-bind="$attrs"
    content-class="order-view-dialog"
    max-width="1024px"
    :persistent="saveInProgress || reportIsLoading"
    :disabled="reportIsLoading"
    :show-icon="false"
  >
    <template #content>
      <v-tabs
        v-model="activeTab"
        class="text-primary text-h4"
        grow
      >
        <v-tab value="documents">
          {{ t("Supporting documents") }}
        </v-tab>
        <v-tab value="summary">
          {{ t("Order Summary Preview") }}
        </v-tab>
      </v-tabs>

      <v-window
        v-model="activeTab"
        class="mt-4"
      >
        <v-window-item value="documents">
          <div
            v-if="dataLoading"
            class="text-center"
          >
            <v-progress-circular
              indeterminate
              color="primary"
            />
          </div>
          <div v-else-if="!dataLoading && order">
            <div
              v-if="!reports || reports.length === 0"
              class="d-flex justify-center"
            >
              {{ t("No documents found") }}
            </div>
            <OrderReportDownload
              v-else
              :loaded-value="loadedValue"
              :reports="reports"
              report-type="supportingFiles"
              @is-loading="loading"
            />
          </div>
        </v-window-item>

        <v-window-item value="summary">
          <OrderSummaryViewer
            v-if="activeTab === 'summary' && order && !dataLoading"
            :order-id="order.orderId"
            :doctor-id="order.doctorId"
            language-id="en"
          />
        </v-window-item>
      </v-window>
    </template>
    <template #actions>
      <OrderSummaryDownload />
    </template>
  </Dialog>
</template>

<script setup lang="ts">
import {computed, ref, useAttrs, watch} from 'vue';
import Dialog from "@/components/dialogs/Dialog.vue";
import OrderReportDownload from "@/components/orders/OrderReportDownload.vue";
import OrderSummaryDownload from "@/components/orders/OrderSummaryDownload.vue";
import OrderSummaryViewer from "@/components/orders/OrderSummaryViewer.vue";
import {Order} from "@/entities/order";
import {Report} from "@/entities/report";
import {useI18n} from 'vue-i18n';

defineOptions({
  name: "OrderViewDialog",
  inheritAttrs: false,
});

interface OrderViewDialogProps {
  dataLoading?: boolean
  order?: Order | null
  reports?: Report[]
  loadedValue?: number | null
}

const {t} = useI18n();

withDefaults(defineProps<OrderViewDialogProps>(), {
  dataLoading: false,
  order: null,
  reports: () => [],
  loadedValue: null,
})

const activeTab = ref('documents');
const showDialog = ref(false);

const attrs = useAttrs();

const reportIsLoading = ref(false);

const saveInProgress = computed(() => !!attrs['save-in-progress']);

watch(showDialog, (value) => {
  if (!value) {
    activeTab.value = 'documents';
  }
});

function loading(isLoading: boolean) {
  reportIsLoading.value = isLoading;
}
</script>

<style lang="scss">
.order-view-dialog {
  .download-order-summary {
    display: flex;
    justify-content: center;
  }
}
</style>
